import { dashboardAPI } from '../utils/api';

/**
 * Fetches service data (income and expenses) for a given date range
 * Using existing dashboard API endpoints
 * @param {Object} params - Query parameters
 * @param {Date} params.startDate - Start date
 * @param {Date} params.endDate - End date
 * @returns {Promise<Object>} Service data with income and expenses arrays
 */
export const getServiceData = async (params) => {
    try {
        const response = await dashboardAPI.getDashboardSummary({
            startDate: params.startDate,
            endDate: params.endDate
        });
        
        const serviceData = response.data?.serviceData || { income: [], expenses: [] };
        return {
            success: true,
            data: {
                income: (serviceData.income || []).map(item => ({
                    id: item.id,
                    name: item.name || '',
                    amount: parseFloat(item.amount || 0)
                })),
                expenses: (serviceData.expenses || []).map(item => ({
                    id: item.id,
                    name: item.name || '',
                    amount: parseFloat(item.amount || 0)
                }))
            }
        };
    } catch (error) {
        console.error('Get service data error:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message,
            data: { income: [], expenses: [] }
        };
    }
};

/**
 * Saves service data (income and expenses)
 * Uses existing dashboard API saveServiceData endpoint
 * @param {Object} payload - Payload containing income, expenses, removed items, and date
 * @param {Array} payload.income - Income items array
 * @param {Array} payload.expenses - Expense items array
 * @param {Array} payload.removed - IDs of removed items
 * @param {string} payload.date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Response with success status
 */
export const saveServiceData = async (payload) => {
    try {
        const response = await dashboardAPI.saveServiceData(payload);
        return {
            success: true,
            message: response.data?.message || 'Veriler kaydedildi',
            data: response.data
        };
    } catch (error) {
        console.error('Save service data error:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message
        };
    }
};

/**
 * Deletes a service data item by ID
 * @param {string|number} itemId - Item ID to delete
 * @returns {Promise<Object>} Response with success status
 */
export const deleteServiceItem = async (itemId) => {
    try {
        const response = await dashboardAPI.deleteServiceTransaction(itemId);
        return {
            success: true,
            message: response.data?.message || 'Veri silindi'
        };
    } catch (error) {
        console.error('Delete service item error:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message
        };
    }
};

/**
 * Gets service data summary for a date range
 * @param {Object} params - Query parameters
 * @param {Date} params.startDate - Start date
 * @param {Date} params.endDate - End date
 * @returns {Promise<Object>} Service data summary
 */
export const getServiceDataSummary = async (params) => {
    try {
        const response = await dashboardAPI.getDashboardSummary({
            startDate: params.startDate,
            endDate: params.endDate
        });
        
        const serviceData = response.data?.serviceData || { income: [], expenses: [] };
        const totalIncome = (serviceData.income || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        const totalExpenses = (serviceData.expenses || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        
        return {
            success: true,
            data: {
                totalIncome,
                totalExpenses,
                netFlow: totalIncome - totalExpenses,
                itemCount: (serviceData.income?.length || 0) + (serviceData.expenses?.length || 0)
            }
        };
    } catch (error) {
        console.error('Get service data summary error:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message,
            data: {
                totalIncome: 0,
                totalExpenses: 0,
                netFlow: 0,
                itemCount: 0
            }
        };
    }
};

const serviceDataService = {
    getServiceData,
    saveServiceData,
    deleteServiceItem,
    getServiceDataSummary
};

export default serviceDataService;
